import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, X, Plus, Trash2, Upload } from "lucide-react";
import "./editItinerary.css";

const API_BASE = "https://crmapi.zauntrip.com/api";

export default function EditItinerary() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    preparedFor: [],
    startDate: "",
    endDate: "",
    totalDays: 0,
    destinations: [],
    notes: "",
    status: "Draft",
    days: [],
    flights: [],
    hotels: [],
    localContacts: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchItinerary();
  }, [id]);

  const fetchItinerary = async () => {
    try {
      setLoading(true);
      // Fetch main itinerary
      const response = await fetch(`${API_BASE}/itineraries/${id}`);
      if (!response.ok) throw new Error("Failed to fetch itinerary");
      
      const data = await response.json();
      if (data.success) {
        setFormData({
          title: data.data.title || "",
          preparedFor: data.data.preparedFor || [],
          startDate: data.data.startDate ? new Date(data.data.startDate).toISOString().split('T')[0] : "",
          endDate: data.data.endDate ? new Date(data.data.endDate).toISOString().split('T')[0] : "",
          totalDays: data.data.totalDays || 0,
          destinations: data.data.destinations || [],
          notes: data.data.notes || "",
          status: data.data.status || "Draft",
          days: data.data.days || [],
          flights: data.data.flights || [],
          hotels: data.data.hotels || [],
          localContacts: data.data.localContacts || []
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTravelerChange = (index, value) => {
    const newTravelers = [...formData.preparedFor];
    newTravelers[index] = value;
    setFormData(prev => ({ ...prev, preparedFor: newTravelers }));
  };

  const addTraveler = () => {
    setFormData(prev => ({
      ...prev,
      preparedFor: [...prev.preparedFor, ""]
    }));
  };

  const removeTraveler = (index) => {
    const newTravelers = formData.preparedFor.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, preparedFor: newTravelers }));
  };

  const handleDestinationChange = (index, field, value) => {
    const newDestinations = [...formData.destinations];
    newDestinations[index][field] = value;
    setFormData(prev => ({ ...prev, destinations: newDestinations }));
  };

  const addDestination = () => {
    setFormData(prev => ({
      ...prev,
      destinations: [...prev.destinations, { city: "", country: "", description: "" }]
    }));
  };

  const removeDestination = (index) => {
    const newDestinations = formData.destinations.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, destinations: newDestinations }));
  };

  // Day handlers
  const handleDayChange = (index, field, value) => {
    const newDays = [...formData.days];
    newDays[index][field] = value;
    setFormData(prev => ({ ...prev, days: newDays }));
  };

  const addDay = () => {
    const newDayNumber = formData.days.length + 1;
    setFormData(prev => ({
      ...prev,
      days: [...prev.days, {
        dayNumber: newDayNumber,
        date: "",
        city: "",
        title: "",
        description: "",
        activities: [],
        mealsIncluded: [],
        inclusions: [],
        exclusions: []
      }]
    }));
  };

  const removeDay = (index) => {
    const newDays = formData.days.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, days: newDays }));
  };

  // Flight handlers
  const handleFlightChange = (index, field, value) => {
    const newFlights = [...formData.flights];
    newFlights[index][field] = value;
    setFormData(prev => ({ ...prev, flights: newFlights }));
  };

  const addFlight = () => {
    setFormData(prev => ({
      ...prev,
      flights: [...prev.flights, {
        flightNumber: "",
        airline: "",
        operatedBy: "",
        fromCity: "",
        toCity: "",
        departureDate: "",
        departureTime: "",
        arrivalDate: "",
        arrivalTime: ""
      }]
    }));
  };

  const removeFlight = (index) => {
    const newFlights = formData.flights.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, flights: newFlights }));
  };

  // Hotel handlers
  const handleHotelChange = (index, field, value) => {
    const newHotels = [...formData.hotels];
    newHotels[index][field] = value;
    setFormData(prev => ({ ...prev, hotels: newHotels }));
  };

  const addHotel = () => {
    setFormData(prev => ({
      ...prev,
      hotels: [...prev.hotels, {
        name: "",
        address: "",
        city: "",
        country: "",
        phone: "",
        images: [],
        roomDetails: []
      }]
    }));
  };

  const removeHotel = (index) => {
    const newHotels = formData.hotels.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, hotels: newHotels }));
  };

  // Contact handlers
  const handleContactChange = (index, field, value) => {
    const newContacts = [...formData.localContacts];
    newContacts[index][field] = value;
    setFormData(prev => ({ ...prev, localContacts: newContacts }));
  };

  const addContact = () => {
    setFormData(prev => ({
      ...prev,
      localContacts: [...prev.localContacts, {
        name: "",
        company: "",
        role: "",
        phone: "",
        email: "",
        address: ""
      }]
    }));
  };

  const removeContact = (index) => {
    const newContacts = formData.localContacts.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, localContacts: newContacts }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Update main itinerary
      const updateData = {
        title: formData.title,
        preparedFor: formData.preparedFor.filter(t => t.trim() !== ""),
        startDate: formData.startDate,
        endDate: formData.endDate,
        totalDays: formData.totalDays,
        destinations: formData.destinations.filter(d => d.city && d.country),
        notes: formData.notes,
        status: formData.status
      };

      const response = await fetch(`${API_BASE}/itineraries/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) throw new Error("Failed to update itinerary");

      // Note: For a complete implementation, you would need to update
      // days, flights, hotels, and contacts separately through their own endpoints
      // This requires more complex state management

      navigate(`/itineraries/${id}`);
    } catch (error) {
      console.error("Error:", error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading itinerary...</p>
      </div>
    );
  }

  return (
    <div className="edit-itinerary">
      <div className="edit-header">
        <button onClick={() => navigate(-1)} className="btn-back">
          <ArrowLeft size={20} />
        </button>
        <h1>Edit Itinerary</h1>
      </div>

      {error && (
        <div className="error-alert">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="btn-close-error">
            <X size={16} />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="section-card">
          <h2>Basic Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="Draft">Draft</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="form-group full-width">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
          </div>
        </div>

        {/* Travelers */}
        <div className="section-card">
          <div className="section-header">
            <h2>Travelers</h2>
            <button type="button" onClick={addTraveler} className="btn-add">
              <Plus size={16} /> Add Traveler
            </button>
          </div>
          {formData.preparedFor.map((traveler, index) => (
            <div key={index} className="array-item">
              <input
                type="text"
                value={traveler}
                onChange={(e) => handleTravelerChange(index, e.target.value)}
                placeholder="Enter traveler name"
              />
              {formData.preparedFor.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeTraveler(index)}
                  className="btn-remove"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Destinations */}
        <div className="section-card">
          <div className="section-header">
            <h2>Destinations</h2>
            <button type="button" onClick={addDestination} className="btn-add">
              <Plus size={16} /> Add Destination
            </button>
          </div>
          {formData.destinations.map((destination, index) => (
            <div key={index} className="destination-form">
              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={destination.city}
                    onChange={(e) => handleDestinationChange(index, "city", e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Country *</label>
                  <input
                    type="text"
                    value={destination.country}
                    onChange={(e) => handleDestinationChange(index, "country", e.target.value)}
                    required
                  />
                </div>
                {formData.destinations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDestination(index)}
                    className="btn-remove"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={destination.description}
                  onChange={(e) => handleDestinationChange(index, "description", e.target.value)}
                  rows="2"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Days (simplified) */}
        <div className="section-card">
          <div className="section-header">
            <h2>Itinerary Days</h2>
            <button type="button" onClick={addDay} className="btn-add">
              <Plus size={16} /> Add Day
            </button>
          </div>
          {formData.days.map((day, index) => (
            <div key={index} className="day-form">
              <div className="day-header">
                <h3>Day {day.dayNumber || index + 1}</h3>
                {formData.days.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDay(index)}
                    className="btn-remove"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={day.date ? new Date(day.date).toISOString().split('T')[0] : ""}
                    onChange={(e) => handleDayChange(index, "date", e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={day.city || ""}
                    onChange={(e) => handleDayChange(index, "city", e.target.value)}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Title</label>
                  <input
                    type="text"
                    value={day.title || ""}
                    onChange={(e) => handleDayChange(index, "title", e.target.value)}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={day.description || ""}
                    onChange={(e) => handleDayChange(index, "description", e.target.value)}
                    rows="3"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-outline"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <>
                <div className="mini-spinner"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}